import React, { Component } from "react";
import client from "@culturehq/client";
import { ModalDialog, Success } from "@culturehq/components";

import { Trans } from "app/utils";

import ErrorMessage from "components/common/ErrorMessage";
import ChqModalDialog from "components/common/ChqModalDialog";
import SurveyItemBuilder from "./SurveyItemBuilder";

const processResponseOptions = (surveyItem, responseOptions) => Promise.all(
  responseOptions.map(async responseOption => {
    if (responseOption.id) {
      return responseOption;
    }

    const { surveyItemResponseOption } = await client.createSurveyItemResponseOption({
      surveyItemId: surveyItem.id,
      body: responseOption.body
    });

    return surveyItemResponseOption;
  })
);

class SurveyItemEditModal extends Component {
  constructor(props) {
    super(props);

    const { surveyItem } = this.props;

    this.state = {
      surveyItem,
      error: false,
      success: false,
      loading: false,
      open: false
    };
  }

  // Note: Re-fetch the surveyItem from the API
  // when the modal is re-opened. This ensures that
  // the surveyItem has the most recently created
  // surveyItemResponseOptions.
  async componentDidUpdate(prevProps) {
    const { loading, surveyItem: currentSurveyItem } = this.state;
    const { id: surveyItemId } = currentSurveyItem || {};

    if (!loading && surveyItemId && (prevProps.surveyItem !== currentSurveyItem)) {
      this.setState({ error: null, loading: true });

      try {
        const { surveyItem } = await client.getSurveyItem({ surveyItemId });
        this.setState({ error: null, loading: false, surveyItem });
      } catch (error) {
        this.setState({ error, loading: false });
      }
    }
  }

  handleOpen = () => {
    this.setState({ open: true });
  };

  handleClose = () => {
    this.setState({ open: false });
  };

  handleSubmit = async surveyItem => {
    const { isEdit, onCallback, survey: { id: surveyId } } = this.props;

    const options = {
      prompt: surveyItem.prompt,
      id: surveyItem.id,
      itemType: surveyItem.itemType,
      surveyId
    };

    // Save response options. Set aside for later.
    let { responseOptions = [] } = surveyItem;

    switch (surveyItem.itemType) {
      case "range":
        options.maxRange = surveyItem.maxRange;
        options.minRange = surveyItem.minRange;
        break;
      case "multi_choice_one":
      case "multi_choice_many":
        if (responseOptions.length < 1) {
          this.setState({ error: "Multi Choice Questions must contain at least one option." });
          return;
        }
        break;
      default:
        break;
    }

    try {
      this.setState({
        loading: true,
        error: false,
        success: false
      });

      let resp;

      // Editing Existing
      if (options.id) {
        options.surveyItemId = options.id;

        resp = await client.updateSurveyItem(options);
      } else {
        // Otherwise, Create
        resp = await client.createSurveyItem(options);
      }

      if (surveyItem.itemType === "multi_choice_one" || surveyItem.itemType === "multi_choice_many") {
        responseOptions = await processResponseOptions(resp.surveyItem, responseOptions);
      } else {
        // reset response options because is not a multichoice item
        responseOptions = undefined;
      }

      if (responseOptions && responseOptions.length) {
        // Submit order
        const order = responseOptions
          .sort((optionA, optionB) => optionA.position - optionB.position)
          .map(({ id }) => id.toString());

        await client.reorderSurveyItemResponseOptions({
          surveyItemId: options.id || resp.surveyItem.id,
          order
        });

        // Note: Since the survey item and the response options
        // are submitted to the API as seperate POST calls,
        // we need to manually ensure that the response options
        // are included on the original surveyItem response. Future
        // GET requests will have the response options populated.
        resp.surveyItem.responseOptions = responseOptions;
      }

      this.setState({
        success: true,
        error: false,
        loading: false
      });

      if (onCallback) {
        const state = { open: false };

        // Reset modal
        if (!isEdit) {
          state.success = false;
        }

        this.setState(state);
        onCallback(resp.surveyItem);
      }
    } catch (error) {
      this.setState({
        loading: false,
        success: false,
        error
      });
    }
  };

  render() {
    const { isEdit, children } = this.props;
    const { surveyItem, error, success, open } = this.state;

    return (
      <>
        {children(this.handleOpen)}
        {open && (
          <ChqModalDialog entrance="zoomIn" className="EditSurveyItemModal" onClose={this.handleClose}>
            <ModalDialog.Heading onClose={this.handleClose}>
              {isEdit ? "Edit Survey Question" : "Create Survey Question"}
            </ModalDialog.Heading>
            <ModalDialog.Body>
              {error && <ErrorMessage error={error} />}
              {success && <Success><Trans>Success!</Trans></Success>}

              <SurveyItemBuilder input={surveyItem} onSubmit={this.handleSubmit} />
            </ModalDialog.Body>
          </ChqModalDialog>
        )}
      </>
    );
  }
}

export default SurveyItemEditModal;
